import React, { useEffect, useState } from 'react';
import { Share2, Plus, X as XIcon } from 'lucide-react';

/**
 * IOSInstallHint
 * ──────────────────────────────────────────────────────────────────
 * Smart bottom-sheet that auto-shows on first iOS Safari visit when the
 * app is NOT yet running in standalone. Closes automatically once the
 * user adds it (Safari fires `visibilitychange` + matchMedia updates).
 *
 * Why separate from InstallAppCard: iOS has NO `beforeinstallprompt`
 * event, so we can't programmatically trigger the native dialog. The
 * closest we can get is a friendly guided tooltip that:
 *   1. Auto-appears once per device
 *   2. Points to the Share button with an animated arrow
 *   3. Shows the exact menu row ("Ana Ekrana Ekle") the user must tap
 *
 * Hidden entirely on:
 *   - Non-iOS devices (Chrome/Edge/Samsung get the real install prompt)
 *   - Already-installed PWAs (display-mode: standalone)
 *   - Non-Safari iOS browsers (Chrome iOS / Firefox iOS can't install PWAs)
 *   - Users who dismissed or followed the hint once (persistent per device)
 */
const DISMISS_KEY = 'bs_ios_install_hint_dismissed_v1';

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  // iOS Safari — not Chrome iOS (CriOS), Firefox iOS (FxiOS), Edge iOS (EdgiOS)
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
  // Genuine Safari has "Safari/" and "Version/" in UA
  return /Safari\//.test(ua) && /Version\//.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const ios = (window.navigator as any).standalone === true;
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(ios || mq);
}

const IOSInstallHint: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (!isIOSSafari()) return;
      if (isStandalone()) return;
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      const t = window.setTimeout(() => setOpen(true), 900);
      return () => window.clearTimeout(t);
    } catch { /* never throw */ }
  }, []);

  // Auto-close when app transitions to standalone (user added to home screen + opened it)
  useEffect(() => {
    if (!open) return;
    const mq = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    const onChange = () => {
      if (isStandalone()) {
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
        setOpen(false);
      }
    };
    mq && mq.addEventListener && mq.addEventListener('change', onChange);
    return () => {
      mq && mq.removeEventListener && mq.removeEventListener('change', onChange);
    };
  }, [open]);

  const dismiss = (permanent: boolean) => {
    if (permanent) {
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="ios-install-hint"
      className="fixed left-0 right-0 bottom-0 z-[2100] p-3 pointer-events-none"
    >
      <style>{`
        @keyframes bsIosSlideUp { from { transform: translateY(100%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes bsIosArrowDown { 0%,100% { transform: translateY(0) } 50% { transform: translateY(6px) } }
        @keyframes bsIosPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(0,122,255,0.55) } 70% { box-shadow: 0 0 0 14px rgba(0,122,255,0) } }
      `}</style>
      <div
        className="pointer-events-auto max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden"
        style={{ animation: 'bsIosSlideUp .35s cubic-bezier(0.22, 1, 0.36, 1)' }}
      >
        <div className="p-4 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#007AFF]/10 text-[#007AFF] flex items-center justify-center">
            <Share2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-stone-900" data-testid="ios-install-hint-title">
                iPhone'a Uygulama Olarak Ekleyin
              </h4>
              <button
                onClick={() => dismiss(false)}
                aria-label="Kapat"
                data-testid="ios-install-hint-close"
                className="w-7 h-7 -mt-1 -mr-1 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 flex items-center justify-center"
              >
                <XIcon size={16} />
              </button>
            </div>
            <p className="text-xs text-stone-600 mt-1 leading-relaxed">
              Bildirimler ve tam ekran deneyim için paneli Ana Ekrana ekleyin — 3 adımda biter.
            </p>

            <ol className="mt-3 space-y-2 text-xs text-stone-700">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-stone-100 text-stone-700 text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                <span className="flex-1">
                  Alt çubuktaki
                  <span
                    className="inline-flex items-center justify-center mx-1 w-6 h-6 rounded-md bg-[#007AFF]/10 text-[#007AFF] align-middle"
                    style={{ animation: 'bsIosPulse 1.8s infinite' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 3v12M8 7l4-4 4 4"/><path d="M5 12v6a2 2 0 002 2h10a2 2 0 002-2v-6"/></svg>
                  </span>
                  <strong>Paylaş</strong> simgesine dokunun
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-stone-100 text-stone-700 text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                <span className="flex-1">
                  Menüden <strong>"Ana Ekrana Ekle"</strong>
                  <span className="inline-flex items-center justify-center w-5 h-5 mx-1 rounded border border-stone-400 text-stone-700 align-middle">
                    <Plus size={10} />
                  </span>
                  seçin
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-stone-100 text-stone-700 text-[10px] font-bold flex items-center justify-center shrink-0">3</span>
                <span className="flex-1">
                  Sağ üstteki <strong>Ekle</strong>'ye basın — simge ana ekranınıza eklenecek
                </span>
              </li>
            </ol>

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => dismiss(true)}
                data-testid="ios-install-hint-dont-show-again"
                className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-[#6b0f1a] text-white hover:bg-[#8a1422] transition-colors"
              >
                Anladım, Tekrar Gösterme
              </button>
              <button
                onClick={() => dismiss(false)}
                data-testid="ios-install-hint-later"
                className="px-3 py-2 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Sonra
              </button>
            </div>
          </div>
        </div>

        {/* Animated arrow pointing down toward the Safari share bar */}
        <div className="flex items-center justify-center pb-2 text-[#007AFF]">
          <div style={{ animation: 'bsIosArrowDown 1.2s ease-in-out infinite' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IOSInstallHint;
